import React from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  ButtonBase,
  Chip,
  Grid,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Description as DescriptionIcon,
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { getDocumentViewLatest } from '../../endpoints/documents';
import { getStorefrontItem } from '../../endpoints/chapterSupplies';
import { useApiGet } from '../../utils/httpClientTs';
import type {
  StorefrontCartAddedLocationState,
  VariantResponse,
} from '../../types/chapterSupplies';
import {
  addItemToCart,
  getCartCount,
  storefrontTextCharCount,
} from '../../utils/chapterSuppliesCart';
import PersonalizationMemberPicker from './PersonalizationMemberPicker';

const formatPrice = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    v || 0
  );

const clipToMaxChars = (val: string, max: number): string =>
  [...val].slice(0, max).join('');
const PERSONALIZATION_NAME_MAX_CHARS = 255;

const variantSort = (a: VariantResponse, b: VariantResponse) =>
  a.sort_order - b.sort_order;

const variantTitle = (v: VariantResponse): string =>
  v.item_name?.trim() || v.description?.trim() || 'Variant option';

/** First non-empty `item_image_url` among variants (sorted), for fallbacks. */
const firstVariantImageAmong = (variants: VariantResponse[]): string => {
  for (const v of variants) {
    const u = v.item_image_url?.trim();
    if (u) return u;
  }
  return '';
};

const StorefrontDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [cartCount, setCartCount] = React.useState(() => getCartCount());
  const [selectedVariantID, setSelectedVariantID] = React.useState<string>('');
  const [customLines, setCustomLines] = React.useState<string[]>([]);
  const [personalizationUserID, setPersonalizationUserID] = React.useState<
    string | null
  >(null);
  const [personalizationDisplayName, setPersonalizationDisplayName] =
    React.useState<string | null>(null);
  const [personalizationFullName, setPersonalizationFullName] =
    React.useState<string>('');
  const [addError, setAddError] = React.useState<string | null>(null);

  const { data, isLoading, error } = useApiGet(
    ['chapter-supplies-storefront-detail', id],
    () => getStorefrontItem(id!),
    { enabled: !!id }
  );

  const libraryDocumentId = data?.library_document_id?.trim() || '';
  const { data: libraryDocument } = useApiGet(
    ['library-document-view', libraryDocumentId],
    () => getDocumentViewLatest(libraryDocumentId),
    { enabled: !!libraryDocumentId, retry: 1 }
  );

  const sortedVariants = React.useMemo(
    () => [...(data?.variants || [])].sort(variantSort),
    [data?.variants]
  );

  const parentFirstImage = React.useMemo(
    () =>
      [...(data?.images || [])]
        .sort((a, b) => a.sort_order - b.sort_order)[0]
        ?.image_url?.trim() || '',
    [data?.images]
  );

  const fallbackVariantImage = React.useMemo(
    () => firstVariantImageAmong(sortedVariants),
    [sortedVariants]
  );

  React.useEffect(() => {
    if (!sortedVariants.length) {
      setSelectedVariantID('');
      return;
    }
    setSelectedVariantID((prev) => {
      if (prev && sortedVariants.some((v) => v.id === prev)) return prev;
      return sortedVariants[0].id;
    });
  }, [sortedVariants]);

  const selectedVariant =
    sortedVariants.find((variant) => variant.id === selectedVariantID) ||
    sortedVariants[0] ||
    null;

  const mainImage = React.useMemo(() => {
    const sel = selectedVariant?.item_image_url?.trim();
    if (sel) return sel;
    if (parentFirstImage) return parentFirstImage;
    return fallbackVariantImage;
  }, [selectedVariant?.item_image_url, parentFirstImage, fallbackVariantImage]);

  const displayPrice = selectedVariant?.price ?? data?.price ?? 0;
  const displayDescription =
    selectedVariant?.description?.trim() ||
    data?.long_description?.trim() ||
    data?.short_description?.trim() ||
    'No description provided.';

  const hasVariantsFlag = data != null && (data.has_variants ?? 0) !== 0;
  const customizationActive =
    data != null &&
    ((data.allows_custom_text ?? 0) !== 0 ||
      (data.allows_personalization ?? 0) !== 0);
  const personalizationRequired =
    data != null && (data.allows_personalization ?? 0) !== 0;
  const effectiveMaxLines = customizationActive
    ? Math.max(1, data!.max_text_lines ?? 1)
    : 0;
  const effectiveMaxChars = customizationActive
    ? Math.max(1, data!.max_text_length ?? 255)
    : 255;

  React.useEffect(() => {
    if (!data) return;
    const active =
      (data.allows_custom_text ?? 0) !== 0 ||
      (data.allows_personalization ?? 0) !== 0;
    const n = active ? Math.max(1, data.max_text_lines ?? 1) : 0;
    setCustomLines(n > 0 ? Array.from({ length: n }, () => '') : []);
    setPersonalizationUserID(null);
    setPersonalizationDisplayName(null);
    setPersonalizationFullName('');
    setAddError(null);
  }, [data]);

  const handlePersonalizationChange = (next: {
    personalization_user_id: string | null;
    personalization_display_name: string | null;
    personalization_full_name: string | null;
  }) => {
    setPersonalizationUserID(next.personalization_user_id);
    setPersonalizationDisplayName(next.personalization_display_name);
    setPersonalizationFullName(next.personalization_full_name || '');
    if (!data || !(data.allows_personalization ?? 0)) return;
    const cap = Math.max(1, data.max_text_lines ?? 1);
    const maxC = Math.max(1, data.max_text_length ?? 255);
    const engraved = clipToMaxChars(next.personalization_full_name || '', maxC);
    setCustomLines((prev) => {
      const lines = [...prev];
      while (lines.length < cap) lines.push('');
      lines[0] = engraved;
      return lines.slice(0, cap);
    });
  };

  const handleAddToCart = () => {
    setAddError(null);
    if (!data) return;
    if (hasVariantsFlag) {
      if (sortedVariants.length === 0) {
        setAddError(
          'This product has variants configured, but none are available yet.'
        );
        return;
      }
      if (!selectedVariant) {
        setAddError('Please choose a variant.');
        return;
      }
    }
    if (customizationActive) {
      const nonEmpty = customLines.map((s) => s.trim()).filter(Boolean);
      if (nonEmpty.length === 0) {
        setAddError(
          'Enter personalization text — at least one line is required.'
        );
        return;
      }
      const capLines = Math.max(1, data.max_text_lines ?? effectiveMaxLines);
      if (nonEmpty.length > capLines) {
        setAddError(`At most ${capLines} personalized lines are allowed.`);
        return;
      }
      for (let i = 0; i < customLines.length; i += 1) {
        const t = customLines[i].trim();
        if (!t) continue;
        if (storefrontTextCharCount(t) > effectiveMaxChars) {
          setAddError(
            `Line ${i + 1} exceeds the maximum length (${effectiveMaxChars} characters).`
          );
          return;
        }
      }
    }
    if (personalizationRequired && !personalizationUserID) {
      setAddError('Select a member for personalization before adding to cart.');
      return;
    }

    addItemToCart(data, {
      quantity: 1,
      online_item_variant_id: selectedVariant?.id ?? null,
      variant_label:
        selectedVariant?.item_name ||
        selectedVariant?.description ||
        selectedVariant?.id ||
        null,
      unit_price: displayPrice,
      custom_text_lines: customizationActive ? [...customLines] : [],
      customization_required: customizationActive,
      max_text_lines: data.max_text_lines,
      max_text_length: data.max_text_length,
      personalization_user_id: personalizationUserID,
      personalization_display_name: personalizationDisplayName,
      personalization_full_name: personalizationFullName || null,
      personalization_required: personalizationRequired,
      image_url: mainImage || undefined,
      variant_image_url: selectedVariant?.item_image_url?.trim() || undefined,
      parent_image_url: parentFirstImage || undefined,
      child_image_url: fallbackVariantImage || undefined,
    });
    setCartCount(getCartCount());

    const itemName = data.name || data.item_name || 'Item';
    const variantLabel =
      hasVariantsFlag && selectedVariant ? variantTitle(selectedVariant) : null;

    navigate('/chapter-supplies/storefront', {
      state: {
        storefrontCartAdded: {
          itemName,
          variantLabel,
          quantity: 1,
        },
      } satisfies StorefrontCartAddedLocationState,
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
      >
        Back to Storefront
      </Button>
      <Box sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={
            <Badge color="primary" badgeContent={cartCount}>
              <ShoppingCartIcon />
            </Badge>
          }
          onClick={() => navigate('/chapter-supplies/storefront/cart')}
        >
          My Cart
        </Button>
      </Box>

      {isLoading && <Typography>Loading item...</Typography>}
      {error && (
        <Typography color="error">Unable to load storefront item.</Typography>
      )}

      {data && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {mainImage ? (
              <Box
                component="img"
                src={mainImage}
                alt={data.name || data.item_name || 'Storefront item'}
                sx={{
                  width: '100%',
                  maxHeight: 440,
                  objectFit: 'contain',
                  bgcolor: 'grey.100',
                }}
              />
            ) : (
              <Box sx={{ width: '100%', height: 320, bgcolor: 'grey.100' }} />
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h4" sx={{ mb: 1 }}>
              {data.name || data.item_name || 'Untitled Item'}
            </Typography>
            <Typography variant="h5" color="primary.main" sx={{ mb: 2 }}>
              {formatPrice(displayPrice)}
            </Typography>

            {sortedVariants.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  Item variants
                </Typography>
                {selectedVariant && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1.5 }}
                  >
                    Selected:{' '}
                    <Box
                      component="span"
                      sx={{ fontWeight: 600, color: 'text.primary' }}
                    >
                      {variantTitle(selectedVariant)}
                    </Box>
                  </Typography>
                )}
                <Box
                  sx={{
                    overflowX: 'auto',
                    pb: 0.5,
                    mx: -0.25,
                    px: 0.25,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1.5}
                    sx={{ alignItems: 'stretch' }}
                  >
                    {sortedVariants.map((v) => {
                      const isSelected = v.id === selectedVariantID;
                      const thumb =
                        v.item_image_url?.trim() ||
                        parentFirstImage ||
                        fallbackVariantImage;
                      const title = variantTitle(v);
                      return (
                        <ButtonBase
                          key={v.id}
                          focusRipple
                          onClick={() => setSelectedVariantID(v.id)}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            width: 120,
                            minWidth: 120,
                            flexShrink: 0,
                            borderRadius: 1,
                            overflow: 'hidden',
                            textAlign: 'left',
                            border: '2px solid',
                            borderColor: isSelected
                              ? 'primary.main'
                              : 'divider',
                            bgcolor: 'background.paper',
                            transition: (theme) =>
                              theme.transitions.create(
                                ['border-color', 'box-shadow'],
                                {
                                  duration: theme.transitions.duration.shorter,
                                }
                              ),
                            boxShadow: isSelected ? 2 : 0,
                          }}
                        >
                          <Box
                            sx={{
                              width: '100%',
                              aspectRatio: '1',
                              bgcolor: 'grey.100',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                            }}
                          >
                            {thumb ? (
                              <Box
                                component="img"
                                src={thumb}
                                alt=""
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'contain',
                                  p: 0.5,
                                }}
                              />
                            ) : (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ px: 0.5 }}
                              >
                                No image
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ p: 1, pt: 0.75 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                lineHeight: 1.25,
                                minHeight: 30,
                              }}
                              title={title}
                            >
                              {title}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="primary"
                              sx={{
                                fontWeight: 700,
                                display: 'block',
                                mt: 0.25,
                              }}
                            >
                              {formatPrice(v.price)}
                            </Typography>
                          </Box>
                        </ButtonBase>
                      );
                    })}
                  </Stack>
                </Box>
              </Box>
            )}
            {hasVariantsFlag && sortedVariants.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This product has variants, but none are configured yet.
              </Typography>
            )}

            <Stack
              direction="row"
              spacing={1}
              sx={{ mb: 2, flexWrap: 'wrap', rowGap: 0.5 }}
            >
              {data.order_approval_required === 1 && (
                <Chip color="warning" label="Order approval required" />
              )}
              <Chip label={data.item_type} variant="outlined" />
            </Stack>

            <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
              {displayDescription}
            </Typography>

            {libraryDocumentId && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Related document
                </Typography>
                <Link
                  component={RouterLink}
                  to={`/library/document/${libraryDocumentId}/latest`}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <DescriptionIcon fontSize="small" aria-hidden />
                  {libraryDocument?.name?.trim() || 'View document'}
                </Link>
              </Box>
            )}

            {customizationActive && effectiveMaxLines > 0 && (
              <Stack spacing={1} sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Personalization</Typography>
                {personalizationRequired ? (
                  <>
                    <Typography variant="caption" color="text.secondary">
                      Select a member and confirm the full name as it should
                      appear on the item (engraving). One line, up to{' '}
                      {effectiveMaxChars} characters.
                    </Typography>
                    <PersonalizationMemberPicker
                      selectedUserID={personalizationUserID}
                      selectedDisplayName={personalizationDisplayName}
                      fullNameValue={personalizationFullName}
                      maxChars={PERSONALIZATION_NAME_MAX_CHARS}
                      onChange={handlePersonalizationChange}
                    />
                  </>
                ) : (
                  <>
                    <Typography variant="caption" color="text.secondary">
                      Up to {effectiveMaxLines} line
                      {effectiveMaxLines === 1 ? '' : 's'}, {effectiveMaxChars}{' '}
                      characters each (at least one non-empty line).
                    </Typography>
                    {customLines.map((val, idx) => (
                      <TextField
                        key={idx}
                        size="small"
                        fullWidth
                        label={`Line ${idx + 1}`}
                        value={val}
                        onChange={(e) => {
                          const next = [...customLines];
                          next[idx] = clipToMaxChars(
                            e.target.value,
                            effectiveMaxChars
                          );
                          setCustomLines(next);
                        }}
                        helperText={`${storefrontTextCharCount(val)} / ${effectiveMaxChars}`}
                      />
                    ))}
                  </>
                )}
              </Stack>
            )}

            {addError && (
              <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
                {addError}
              </Alert>
            )}

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ mb: 2 }}
            >
              <Button variant="contained" onClick={handleAddToCart}>
                Add to Cart
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/chapter-supplies/storefront/cart')}
              >
                Go to Cart
              </Button>
            </Stack>

            {data.max_quantity_per_order != null &&
              data.max_quantity_per_order > 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Max quantity per order: {data.max_quantity_per_order}
                </Typography>
              )}
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default StorefrontDetail;
